/* eslint-disable react/display-name */
import {
  DeleteTwoTone,
  DownloadOutlined,
  ExclamationCircleTwoTone,
} from '@ant-design/icons';
import { Button, Popconfirm, Table, Tooltip } from 'antd';
import React, { Fragment, useContext, useEffect, useState } from 'react';
import { isEmpty } from 'underscore';
import Api from '../../../../api/Api';
import { StoreContext } from '../../../../store/store';
import { CartTableItem } from '../../../../typings/home';
import styles from '../../Style/ShoppingCart.module.scss';
import CartDetails from './CartDetails';

export default function CartTable(props: {
  tableData: CartTableItem[];
  delete: (item: CartTableItem) => {};
  loading: boolean;
}) {
  const ERRORCOLUMN = 'Status';
  const { setSearchId, setShowSearch } = useContext(StoreContext);

  const { tableData, loading } = props;

  const handleDelete = (item: CartTableItem) => {
    props.delete(item);
  };

  const [columns, setColumns] = useState([
    {
      title: 'Point Name',
      dataIndex: 'pointName',
      key: 'pointName',
      className: 'PointNameColumn',
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
    },
    {
      title: 'Dataset',
      dataIndex: 'acronym',
      key: 'acronym',
      className: 'DatasetColumn',
      render: (_text: any, record: CartTableItem, _index: any) => (
        <Tooltip title={'Click to see metadata'}>
          <span onClick={() => onPointNameClick(record)}>
            {record.acronym ? record.acronym : record.Dataset}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'Sample data (.csv)',
      key: 'sample',
      render: (_text: any, record: CartTableItem, _index: any) => (
        <Button
          disabled={record.isFwe}
          type="primary"
          icon={<DownloadOutlined />}
          size={'small'}
          onClick={async () => window.open(await getSampleDataLink(record))}
        >
          Download
        </Button>
      ),
    },
    {
      title: 'Location',
      dataIndex: 'Location',
      key: 'Location',
    },
    {
      title: 'Time',
      dataIndex: 'TimeStr',
      key: 'TimeStr',
    },
    {
      title: 'Price',
      dataIndex: 'Price',
      key: 'Price',
      render: (_text: any, record: CartTableItem, _index: number) =>
        `€ ${record.Price.toFixed(2)}`,
    },
    {
      title: 'Operation',
      dataIndex: 'Operation',
      key: 'Operation',
      width: '125px',
      render: (_text: any, record: CartTableItem, _index: any) => (
        <Popconfirm
          placement="left"
          title="Are you sure you want to remove this item?"
          onConfirm={() => handleDelete(record)}
          okText="Yes"
          cancelText="No"
        >
          <Button
            key={Math.random()}
            type="link"
            icon={<DeleteTwoTone style={{ color: '#666666' }} />}
            style={{ padding: 0 }}
            loading={record.loading}
          >
            REMOVE
          </Button>
        </Popconfirm>
      ),
    },
  ]);

  const errorColumn = {
    title: ERRORCOLUMN,
    dataIndex: 'hasError',
    key: 'hasError',
    className: '',
    width: undefined,
    render: (_text: any, record: CartTableItem, _index: any) => (
      <Fragment>
        {record.hasError ? (
          <Button
            key={Math.random()}
            type="link"
            icon={<ExclamationCircleTwoTone style={{ color: '#666666' }} />}
            style={{ padding: 0 }}
            loading={record.isPending}
          >
            {record.isPending ? 'Pending' : 'Failed'}
          </Button>
        ) : null}
      </Fragment>
    ),
  };

  useEffect(() => {
    if (tableData && tableData.length > 0) {
      const errors = tableData.find((item: CartTableItem) => item.hasError);
      const removeError = columns.filter(
        (column) => column.title !== ERRORCOLUMN
      );
      if (errors) {
        setColumns([...removeError, errorColumn]);
      } else {
        setColumns(removeError);
      }
    }
  }, [tableData]);

  function onPointNameClick(record: CartTableItem) {
    if (isEmpty(record.datasetId)) {
      return;
    }
    setSearchId(record.datasetId);
    setShowSearch(true);
  }

  const getSampleDataLink = async (record: CartTableItem) => {
    if (record.datasetId) {
      const meta = await Api.detail.searchDatasetDetail(record.datasetId);
      const link =
        meta &&
        meta['Variables_Sample Data (.csv)'] &&
        meta['Variables_Sample Data (.csv)'].link
          ? meta['Variables_Sample Data (.csv)'].link
          : '';
      return link;
    }
  };

  return (
    <Table
      size="small"
      bordered
      dataSource={tableData}
      columns={columns}
      className={styles.cartTable}
      pagination={{ defaultPageSize: 5 }}
      expandedRowRender={(record: CartTableItem) => (
        <CartDetails data={record.Detail}></CartDetails>
      )}
      expandedRowKeys={tableData.filter((d) => d.showDetail).map((d) => d.key)}
      expandIconColumnIndex={-1}
      expandIcon={() => null}
      loading={loading}
    />
  );
}
